import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isEqual } from 'lodash-es'

import useLocalStorage from 'hooks/use-local-storage'
import { googleapis } from 'services/geolocation'
import { useCountries } from 'queries/address-lookup'
import { STALE_TIME } from 'utils/constants'

export type LocationData = {
  latitude: number | null
  longitude: number | null
  accuracy: number | null
}

const DEFAULT_CURRENT_POSITION: LocationData = {
  latitude: null,
  longitude: null,
  accuracy: null,
}

/**
 * Options for configuring the geolocation API.
 *
 * See [Geolocation.getCurrentPosition()](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition)
 * for more details on possible options.
 */
const DEFAULT_POSITION_OPTIONS = {
  timeout: 10000,
  maximumAge: 0,
  enableHighAccuracy: true,
}

export const LOCATION_ERROR_CODE = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3,
}

const useGeoLocation = () => {
  const [watchId, setWatchId] = useLocalStorage('CURRENT_LOCATION_WATCH_ID', 0)
  const [currentLocation, setCurrentLocation] = useLocalStorage<LocationData>(
    'CURRENT_LOCATION',
    DEFAULT_CURRENT_POSITION,
  )
  const [error, setError] = useLocalStorage<GeolocationPositionError | null>('CURRENT_LOCATION_ERROR', null)

  useEffect(
    () =>
      // Cleanup on unmount
      () => {
        if (watchId) {
          navigator.geolocation.clearWatch(watchId)
        }
      },
    [watchId],
  )

  const resetCurrentPosition = () => {
    if (isEqual(currentLocation, DEFAULT_CURRENT_POSITION)) {
      return
    }
    setCurrentLocation(DEFAULT_CURRENT_POSITION)
  }

  const getCurrentPosition = async (): Promise<typeof DEFAULT_CURRENT_POSITION> =>
    new Promise((resolve, reject) => {
      if (
        currentLocation.latitude &&
        currentLocation.longitude &&
        currentLocation.accuracy &&
        currentLocation.accuracy < 100
      ) {
        resolve(currentLocation)
      }

      if (watchId) {
        navigator.geolocation.clearWatch(watchId)
      }

      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude, accuracy } }) => {
          if (currentLocation.accuracy && accuracy < currentLocation.accuracy) {
            const newLocation = {
              latitude,
              longitude,
              accuracy,
            }

            setCurrentLocation(newLocation)
            setError(null)
            resolve(newLocation)
          }

          resolve(currentLocation)
        },
        (e: GeolocationPositionError) => {
          if (!currentLocation.latitude && !currentLocation.longitude) {
            reject(e)
            setError(e)
          }

          resolve(currentLocation)
        },
        DEFAULT_POSITION_OPTIONS,
      )

      // Restart watching for future position changes
      watchLocation()
    })

  const watchLocation = () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId)
    }

    const id = navigator.geolocation.watchPosition(
      ({ coords: { latitude, longitude, accuracy } }) => {
        setCurrentLocation({
          latitude,
          longitude,
          accuracy,
        })
        setError(null)
      },
      (e) => {
        let errMsg = e.message
        if (e instanceof GeolocationPositionError) {
          switch (e.code) {
            case LOCATION_ERROR_CODE.PERMISSION_DENIED:
              errMsg = 'User denied the request for Geolocation.'
              resetCurrentPosition()
              break
            case LOCATION_ERROR_CODE.TIMEOUT:
              errMsg = 'The request to get user location timed out.'
              setCurrentLocation({
                latitude: null,
                longitude: null,
                accuracy: -1,
              })
              break
            case LOCATION_ERROR_CODE.POSITION_UNAVAILABLE:
              errMsg = 'Location information is unavailable.'
              setCurrentLocation({
                latitude: null,
                longitude: null,
                accuracy: -2,
              })
              break
            default:
              errMsg = 'Location error.'
              setCurrentLocation({
                latitude: null,
                longitude: null,
                accuracy: -3,
              })
              break
          }
        }
        console.error(`Location watch: ${errMsg}`)
        setError({
          ...e,
          code: e.code,
          message: errMsg,
        })
      },
      {
        ...DEFAULT_POSITION_OPTIONS,
        timeout: 20000,
      },
    )

    setWatchId(id)
  }

  return {
    currentLocation,
    getCurrentPosition,
    resetCurrentPosition,
    watchLocation,
    error,
  }
}

export const useGeoCode = (shouldFetch = true) => {
  const { currentLocation } = useGeoLocation()
  const response = useQuery(
    ['google-geolocation', { lat: currentLocation.latitude, lon: currentLocation.longitude }],
    () => googleapis({ lat: currentLocation.latitude ?? 0, lon: currentLocation.longitude ?? 0 }),
    {
      enabled: shouldFetch && !!currentLocation.latitude && !!currentLocation.longitude,
      staleTime: STALE_TIME.masterSetting,
    },
  )
  return response
}

export const useGeoCountry = () => {
  const { data: countries } = useCountries()
  const { data: geoCode } = useGeoCode()

  if (!geoCode?.country.code || !countries) {
    return null
  }

  return countries?.find((c) => c.iso2 === geoCode?.country.code)
}

export default useGeoLocation
